import api from '@core/services/api/_api';
import util from '@core/services/util';
import ENUMS from '@core/classes/Enums';

const DropdownHelper = {
	/// Label will say "Default" when no state is given
	getLenders: async () => {
		return await DropdownHelper.$cache.getSet('GET_LENDERS', async () => {
			let data = null;

			let apiResponse = await api.settings.getLenders();
			if (apiResponse && apiResponse.data) {
				return apiResponse.data;
			}
			return data;
		});
	},

	getDealTypes: async () => {
		return await DropdownHelper.$cache.getSet('GET_DEAL_TYPES', async () => {
			return ['FINANCE', 'LEASE', 'CASH'].map(c => ({
				value: c,
				display: c,
			}));
		});
	},

	getInventoryTypes: async () => {
		return await DropdownHelper.$cache.getSet('GET_INVENTORY_TYPES', async () => ['New', 'Used', 'Certified']);
	},

	getBrands: async (forceRefresh = false) => {
		return await DropdownHelper.$cache.getSet(
			'GET_BRANDS_DROPDOWN',
			async () => {
				let data = null;
				let apiResponse = await api.settings.getBrands();
				if (apiResponse && apiResponse.data) {
					data = apiResponse.data.map(c => {
						return {
							value: c,
							display: c.brandName,
						};
					});
				}
				return data;
			},
			forceRefresh,
		);
	},
	getModels: async (forceRefresh = false) => {
		return await DropdownHelper.$cache.getSet(
			'GET_MODELS_DROPDOWN',
			async () => {
				let data = null;
				let apiResponse = await api.settings.getModels();
				if (apiResponse && apiResponse.data) {
					data = apiResponse.data.map(c => {
						return {
							value: c,
							display: c.modelName,
						};
					});
				}
				return data;
			},
			forceRefresh,
		);
	},
	getCarCalcsTitleIdsDropDown: async () => {
		return await DropdownHelper.$cache.getSet('GET_CARCALCS_TITLEIDS_DROPDOWN', async () => {
			let dropdownData = null;
			let apiResponse = await api.utilities.getCarCalcsTitleIds();
			console.log(apiResponse);
			if (apiResponse && apiResponse.data) {
				dropdownData = apiResponse.data.map(c => {
					return {
						value: c.titleId,
						display: `${c.titleId} - ${c.name}${c.alternativeNames ? ' - ' + c.alternativeNames : ''}`,
					};
				});
			}
			return dropdownData;
		});
	},
	getAdditionTypes: () => {
		return ENUMS.ADDITION_TYPES.toList();
	},
	getValidationListForCBCList: () => {
		return ["Driver's License", 'Passport', 'Social Security Card', 'Vehicle Title/Reg', 'Utility Bill', 'Credit Thawed/Unfrozen', 'Other'];
	},

	getCBCCredVersions: () => {
		let disabledList = ENUMS.CBC_CREDIT_PULL_VERSIONS_FICO.toList().map(vr => (vr.text.includes('--') ? { ...vr, disabled: true } : { ...vr, disabled: false }));

		// lets separate the groups in between the disabled: true (these are separators for the dropdown)
		let groups = [];
		let currentGroup = [];

		disabledList.forEach(item => {
			if (item.disabled) {
				// If there are items in the current group, add it to groups
				if (currentGroup.length > 0) {
					groups.push(currentGroup);
					currentGroup = [];
				}
				// Start a new group with the disabled item
				groups.push([item]);
			} else {
				// Add item to the current group
				currentGroup.push(item);
			}
		});

		if (currentGroup.length > 0) {
			groups.push(currentGroup);
		}

		// Per request, sorting the List based on the numbers, so the highest number in the credit report version type will be displayed first
		let sortedGroups = groups.map(group => {
			// Check if the group is not just a separator
			if (group.length > 1 || !group[0].disabled) {
				return group.sort((a, b) => {
					// Extract numbers from the text of a and b
					const numA = util.extractNumber(a.text);
					const numB = util.extractNumber(b.text);
					return numB - numA; // Sort in descending order
				});
			}
			return group; // Return the group unchanged if it's just a disabled item
		});

		return sortedGroups.flat();
	},

	getCBCCredVersionsForStoreSettings: () => {
		let disabledList = ENUMS.CBC_CREDIT_PULL_VERSIONS_FICO.toList().map(vr => (vr.text.includes('--') ? { ...vr, disabled: true } : { ...vr, disabled: false }));

		// Remove the disabled ones from the list
		let enabledList = disabledList.filter(item => !item.disabled);

		// Function to extract the group abbreviation from the text
		const getAbbreviation = text => {
			if (text.includes('EFX')) return 'EFX'; // Example: "FICO Auto 5 EFX N"
			if (text.includes('XPN')) return 'XPN'; // Example: "FICO Auto V2 XPN"
			if (text.includes('TU')) return 'TU'; // Example: "FICO Auto Score 4 TU"
			// Add more conditions for other groups as needed
			return ''; // Default value
		};

		// Add abbreviation field to each item
		enabledList.forEach(item => {
			item.abbreviation = getAbbreviation(item.text);
		});

		// Let's separate the groups in between the disabled: true (these are separators for the dropdown)
		let groups = [];
		let currentGroup = [];

		enabledList.forEach(item => {
			if (currentGroup.length > 0 && item.text.includes('--')) {
				groups.push(currentGroup);
				currentGroup = [];
			}
			currentGroup.push(item);
		});

		if (currentGroup.length > 0) {
			groups.push(currentGroup);
		}

		// Per request, sorting the List based on the numbers, so the highest number in the credit report version type will be displayed first
		let sortedGroups = groups.map(group => {
			// Check if the group is not just a separator
			if (group.length > 1 || !group[0].disabled) {
				return group.sort((a, b) => {
					// Extract numbers from the text of a and b
					const numA = util.extractNumber(a.text);
					const numB = util.extractNumber(b.text);
					return numB - numA; // Sort in descending order
				});
			}
			return group; // Return the group unchanged if it's just a disabled item
		});

		// Flatten the sorted groups array into a single array
		let flattenedList = sortedGroups.reduce((acc, val) => acc.concat(val), []);

		return flattenedList;
	},

	getValidationListForDOB: () => {
		return ["Driver's License", 'Passport', 'Other'];
	},
	getValidationListForSSN: () => {
		return ['Passport', 'Social Security Card', 'Other'];
	},
	getValidationListForAddress: () => {
		return ["Driver's License", 'Passport', 'Vehicle Title/Reg', 'Utility Bill', 'Other'];
	},
	getValidationListForFraud: () => {
		return ["Driver's License", 'Passport', 'Social Security Card', 'Credit Thawed/Unfrozen', 'Other'];
	},

	getRouteOneTaxesList: () => {
		return [
			'luxuryTax',
			'salesTax',
			'totalSalesUseTax',
			'securityDepositTax',
			'capCostReductionTax',
			'monthlyUseTax',
			'documentStampTax',
			'motorVehicleTax',
			'upfrontFeeTax',
			'vehicleInventoryTax',
			'exciseTax',
			'wheelTax',
			'localOptionSalesTax',
			'localOption',
			'countyTax',
			'taxNotIncludedInCashPrice',
			'taxNotIncludedInCashPrice2',
			'taxNotIncludedInCashPrice3',
			'singleArticleTax',
			'propertyTax',
			'otherTax',
			'titleAdValoremTax',
			'grossReceiptsTax',
			'serviceContractTax',
		];
	},
	getRouteOneFeesList: () => {
		return [
			'acquisitionFee',
			'administrationFee',
			'arbitrationFee',
			'batteryFee',
			'convenienceFee',
			'dealerProcessingFee',
			'deliveryHandlingFee',
			'deputyServicFeePaidToDealer',
			'deqServiceFee',
			'documentFee',
			'electronicFilingFee',
			'emissionInspectionFee',
			'filingFee',
			'lenderMemberFee',
			'licenseFee',
			'lienNotationFee',
			'motorVehicleInspectionFee',
			'officialFees',
			'otherFees',
			'plateFee',
			'processingFee',
			'registrationFee',
			'safetyInspectionFee',
			'securityInterestChargesFilingFee',
			'serviceHandlingFee',
			'smogCertificationPaidToSeller',
			'smogCertificationPaidToState',
			'smogExpemptionDonatonFee',
			'suplamentalTitleFee',
			'tireFee',
			'titleRegistrationProcessingFee',
			'titleFee',
			'transferFee',
			'officialFees2',
			'officialFees3',
			'dispositionFee',
			'ERTFee',
			'messengerFee',
			'motorVehicleWarrantyTrustFundFee',
			'notaryFee',
			'purchaseOptionFee',
			'rentalSurchargeFee',
			'temporaryTagFee',
			'totalFees',
			'freightFee',
			'georgiaMotorVehicleWarrantyRightsActFee',
			'loanOriginationFee',
			'nonFilingInsuranceFee',
			'onlineRegistrationFee',
			'roadAndBridgeFee',
			'buyersTagFee',
			'childSafetySeatFee',
			'emergencyMedicalServiceAdministrationFee',
			'exemptionFee',
			'infrastructureMaintenanceFee',
			'localFee',
			'electronicTempTagProcessingFee',
			'uDriveItFee',
			'airQualityFee',
			'courtesyDeliveryFee',
			'exemptionFee',
			'titlePrepFee',
			'monthlyLocalFee',
			'motorVehicleInspectionFeePaidToStation',
		];
	},
	getDocumentPacketCategories: async () => {
		return await DropdownHelper.$cache.getSet(
			'DOCUMENT_PACKET_CATEGORIES',
			async () => {
				let data = null;

				const response = await api.utilities.getPaperworkCategories();
				const paperworkCategories = response.data.sort((a, b) => a.orderIndex - b.orderIndex);

				if (paperworkCategories) {
					data = paperworkCategories.map(c => {
						return {
							value: c,
							display: c.description,
						};
					});
				}
				return data;
			},
			true,
		);
	},
	getProfitFlags: () => {
		return ENUMS.PROFIT_FLAGS.toList();
	},
	getVehicleDeliveryMethods: () => {
		return ENUMS.VEHICLE_DELIVERY_METHODS.toList();
	},
	$cache: {
		getSet: async (CACHE_KEY, dataFunction, forceRefresh = false) => {
			//CHECK IF WE HAVE IT IN CACHE
			let dropdownData = null;
			if (forceRefresh == false) {
				let dropdownData = DropdownHelper.$cache.get(CACHE_KEY) ?? null;

				if (dropdownData) {
					return dropdownData;
				}
			}

			//CALL THE SERVER - GET NEW DATA
			dropdownData = await dataFunction();
			if (dropdownData) {
				//SAVE TO CACHE
				DropdownHelper.$cache.set(CACHE_KEY, dropdownData);
			}

			return dropdownData;
		},
		get: key => {
			let currDate = new Date();
			let cachedItem = DropdownHelper.$cache.items[key] ?? null;
			if (cachedItem && cachedItem.exp > currDate) return cachedItem.data;
			else return null;
		},
		set: (key, data, expirationInMinutes = 30) => {
			let expirationDate = new Date();
			expirationDate = expirationDate.setTime(expirationDate.getTime() + expirationInMinutes * 60 * 1000);

			let newCacheItem = {
				key: key,
				data: data,
				exp: expirationDate,
			};

			DropdownHelper.$cache.items[key] = newCacheItem;
		},
		items: {},
	},
};

export default DropdownHelper;
